import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const MemberAuthContext = createContext(null);

export function MemberAuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount (cookie-based)
  useEffect(() => {
    if (!localStorage.getItem('memberLoggedIn')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const data = await apiService.getMemberCurrent();
        setMember(data.member);
      } catch {
        // Session expired or invalid - clear the flag
        localStorage.removeItem('memberLoggedIn');
      }
      setLoading(false);
    };

    checkAuth();

    // Re-validate session whenever the tab becomes visible again.
    // Browsers can discard idle tabs and reload them; if the token has
    // expired by then, we clear auth state immediately rather than
    // leaving the member on a broken page.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('memberLoggedIn')) {
        apiService.getMemberCurrent()
          .then(data => setMember(data.member))
          .catch(() => {
            localStorage.removeItem('memberLoggedIn');
            setMember(null);
          });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const signup = useCallback(async ({ email, password, firstName, lastName, recaptchaToken }) => {
    setError(null);
    try {
      const data = await apiService.memberSignup({ email, password, firstName, lastName, recaptchaToken });

      if (data.requiresVerification) {
        return { requiresVerification: true, message: data.message };
      }

      const { member: memberData } = data;
      setMember(memberData);
      localStorage.setItem('memberLoggedIn', '1');

      return { success: true, member: memberData };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Signup failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await apiService.memberLogin({ email, password });

      if (data.requiresVerification) {
        throw new Error(data.error || 'Please verify your email before logging in.');
      }

      const { member: memberData } = data;
      setMember(memberData);
      localStorage.setItem('memberLoggedIn', '1');

      return memberData;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.memberLogout();
    } catch {
      // Clear local state even if server call fails
    }
    localStorage.removeItem('memberLoggedIn');
    setMember(null);
  }, []);

  const verifyEmail = useCallback(async (token) => {
    setError(null);
    try {
      const data = await apiService.memberVerifyEmail(token);
      const { member: memberData } = data;
      setMember(memberData);
      localStorage.setItem('memberLoggedIn', '1');

      return memberData;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Email verification failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const resendVerification = useCallback(async (email) => {
    setError(null);
    try {
      const data = await apiService.memberResendVerification(email);
      return data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to send verification email';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    setError(null);
    try {
      const data = await apiService.memberForgotPassword(email);
      return data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to send reset email';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    setError(null);
    try {
      const data = await apiService.memberResetPassword(token, password);
      return data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to reset password';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);
    try {
      const data = await apiService.memberChangePassword(currentPassword, newPassword);
      return data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to change password';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const updateMember = useCallback((memberData) => {
    setMember(prev => ({ ...prev, ...memberData }));
  }, []);

  const refreshMember = useCallback(async () => {
    try {
      const data = await apiService.getMemberProfile();
      const memberData = data.profile || data.member;
      setMember(memberData);
      return memberData;
    } catch (err) {
      console.error('Failed to refresh member:', err);
      return null;
    }
  }, []);

  const value = {
    member,
    loading,
    error,
    signup,
    login,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    changePassword,
    updateMember,
    refreshMember,
    isAuthenticated: !!member,
    clearError: () => setError(null),
  };

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
}

export function useMemberAuth() {
  const context = useContext(MemberAuthContext);
  if (!context) {
    throw new Error('useMemberAuth must be used within a MemberAuthProvider');
  }
  return context;
}

// Component wrapper for member-only routes
export function RequireMemberAuth({ children, fallback }) {
  const { isAuthenticated, loading } = useMemberAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (fallback) return fallback;
    window.location.href = '/member/login';
    return null;
  }

  return children;
}

export default MemberAuthContext;
